import "./bulkRequestTable.scss";
import { Empty } from "antd";
import { VyneTable } from "@vyne-shared/components/vyneTable/VyneTable";
import { getBulkRequestColumns } from "./BulkRequestColumns";
import { useRowSelection } from "./useRowSelection";
import { validateRecord } from "../bulkRequestUtils";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { FrozenSetDispatch } from "utils/useFrozenState";
import type { SaveCallback } from "../useChangeOverlay";
import type { StrictlyFrozenSet } from "utils/sets";

interface BulkRequestTableProps {
    readonly isPending: boolean;
    readonly combinedData: BulkRequestRecord[];
    readonly handleSave: SaveCallback;
    readonly selectedRows: StrictlyFrozenSet<string>;
    readonly setSelectedRows: FrozenSetDispatch<string>;
    readonly selectableSet: StrictlyFrozenSet<string>;
}

export const BulkRequestTable = ({
    isPending,
    combinedData,
    handleSave,
    selectedRows,
    setSelectedRows,
    selectableSet,
}: BulkRequestTableProps) => (
    <VyneTable
        name="Outstanding Accounts Table"
        loading={isPending}
        dataSource={combinedData}
        columns={getBulkRequestColumns(handleSave)}
        className="bulk-request-table"
        rowKey="pmsId"
        rowClassName={rowClassName}
        rowSelection={useRowSelection(selectedRows, setSelectedRows, selectableSet)}
        pagination={{
            defaultPageSize: 50,
            hideOnSinglePage: false,
            pageSizeOptions: [10, 50, 100, 500],
            total: combinedData.length,
            showTotal: (total: number) => `Total ${total} items`,
            position: ["bottomRight"],
        }}
        locale={{ emptyText: <Empty description="No patients found." /> }}
    />
);

/** Style rows so that they appear disabled when communication is disabled. */
function rowClassName(record: BulkRequestRecord) {
    let rowClass = !record.allowCommunication ? "bulk-request-disabled" : "";

    // validate the record the same way we do for the BulkRequestSubmitButton
    const isRecordValid = validateRecord(record);
    if (!isRecordValid) {
        rowClass += " contact-validation-error";
        return rowClass;
    }

    if (record.hasOutstandingClaims) {
        rowClass += " bulk-request-outstanding-claim";
    }

    return rowClass;
}
